import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import ReactDOM from 'react-dom';
import { motion } from 'framer-motion';
import CreateCollaborationModal from '../components/collaboration/CreateCollaborationModal';
import ConfirmationModal from '../components/common/ConfirmationModal';
import { IS_INSTAGRAM_SYNC_ENABLED } from '../utils/featureFlags';
import {
  UserCircleIcon,
  LockClosedIcon,
  BellIcon,
  EyeIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  AdjustmentsHorizontalIcon,
  NoSymbolIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  ShareIcon,
  DocumentTextIcon,
  PlusIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  IdentificationIcon,
  CheckCircleIcon,
  StarIcon,
  CreditCardIcon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import Avatar from '../components/common/Avatar';
import SocialShareModal from '../components/common/SocialShareModal';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { updateUser, logoutUser } from '../store/slices/authSlice';
import { deleteAccount } from '../store/slices/userSlice';
import { userAPI, crmAPI, subscriptionAPI, API_BASE_URL } from '../services/api';
import { resolveImageUrl } from '../utils/image';
import { compressImage } from '../utils/compression';
import toast from 'react-hot-toast';
import Button from '../components/common/Button';
import { useNavigate, useSearchParams, useParams, Link } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import ThemeToggle from '../components/common/ThemeToggle';

type BillingOrder = {
  id: string;
  razorpay_order_id: string;
  razorpay_payment_id: string;
  amount: number;
  currency: string;
  plan_tier: string;
  billing_cycle: string;
  status: string;
  created_at: string;
};

type TabType = 'account' | 'privacy' | 'notifications' | 'security' | 'preferences' | 'blocked' | 'referrals' | 'business' | 'contracts' | 'data' | 'support' | 'billing';

const SettingsPage: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { tab: urlTab } = useParams<{ tab: string }>();
  const isModel = user?.user_type === 'model';
  
  const [activeTab, setActiveTab] = useState<TabType>((urlTab as TabType) || (searchParams.get('tab') as TabType) || 'account');
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  
  
  // Account Settings State
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.phone || '');
  
  // Password Change State
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Privacy Settings State
  const [displayEmail, setDisplayEmail] = useState(user?.display_email ?? true);
  const [displayPhone, setDisplayPhone] = useState(user?.display_phone_number ?? true);
  
  // Notification Settings State
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(true);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  
  // Image Quality Settings
  const [imageQuality, setImageQuality] = useState<'original' | 'medium'>(user?.image_quality_preference || 'medium');
  const [defaultPortfolioView, setDefaultPortfolioView] = useState<'folders' | 'grid'>(user?.default_portfolio_view || 'folders');
  const [defaultPortfolioTab, setDefaultPortfolioTab] = useState<'photos' | 'videos'>(user?.default_portfolio_tab || 'photos');

  // Business Settings State
  const [companyName, setCompanyName] = useState(user?.company_name || '');
  const [gstNumber, setGstNumber] = useState(user?.gst_number || '');
  const [gstPercentage, setGstPercentage] = useState(user?.gst_percentage || 18);
  const [businessLogo, setBusinessLogo] = useState<string>(user?.business_logo_signed || '');
  const [logoUploading, setLogoUploading] = useState(false);
  const [address, setAddress] = useState(user?.address || '');
  
  // Support State
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketMessage, setTicketMessage] = useState('');
  const [submittingTicket, setSubmittingTicket] = useState(false);

  // Billing History State
  const [billingOrders, setBillingOrders] = useState<BillingOrder[]>([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [fetchingHistory, setFetchingHistory] = useState(false);

  

  // Shared helper to refresh profile after Instagram is connected
  const fetchFreshProfile = useCallback(async () => {
    try {
      const response = await userAPI.getProfile();
      const userData = response.data?.data?.user || response.data?.data || response.data;
      if (userData && (userData.id || userData.email)) {
        dispatch(updateUser(userData));
      }
    } catch (error) {
      console.error('Failed to refresh profile after Instagram login:', error);
    }
  }, [dispatch]);

  // Connections State (Google Drive & Instagram Media Access Toggle)
  const [driveConnected, setDriveConnected] = useState(false);
  const [driveAccountName, setDriveAccountName] = useState('');
  const [driveLoading, setDriveLoading] = useState(false);
  const [mediaAccessEnabled, setMediaAccessEnabled] = useState(user?.instagram_media_access_enabled ?? false);
  const [highlightMediaAccess, setHighlightMediaAccess] = useState(false);
  const [showDisconnectIgModal, setShowDisconnectIgModal] = useState(false);
  const mediaAccessRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user?.instagram_media_access_enabled !== undefined) {
      setMediaAccessEnabled(user.instagram_media_access_enabled);
    }
  }, [user?.instagram_media_access_enabled]);

  useEffect(() => {
    const checkDriveStatus = async () => {
      setDriveLoading(true);
      try {
        const res = await crmAPI.getGoogleDriveStatus();
        if (res.data?.connected) {
          setDriveConnected(true);
          setDriveAccountName(res.data?.account_email || res.data?.account_name || 'Connected');
        } else {
          setDriveConnected(false);
          setDriveAccountName('');
        }
      } catch (err) {
        console.error('Failed to check Google Drive status:', err);
      } finally {
        setDriveLoading(false);
      }
    };
    checkDriveStatus();
  }, [user?.email]);

  const handleConnectDrive = async () => {
    try {
      const authRes = await crmAPI.getGoogleDriveAuthURL();
      if (authRes.data?.url) {
        window.location.href = authRes.data.url;
      } else {
        toast.error('Failed to obtain Google Drive authorization link');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to initiate Google Drive connection');
    }
  };

  const handleToggleMediaAccess = async (enabled: boolean) => {
    setMediaAccessEnabled(enabled);
    try {
      await userAPI.updateProfile({ instagram_media_access_enabled: enabled });
      dispatch(updateUser({ instagram_media_access_enabled: enabled }));
      toast.success(enabled ? 'Instagram media access allowed' : 'Instagram media access paused');
    } catch (error: any) {
      setMediaAccessEnabled(!enabled);
      toast.error(error.response?.data?.message || 'Failed to update media access setting');
    }
  };

  // Check for successful Instagram connection redirect (web flow)
  useEffect(() => {
    if (searchParams.get('instagram_connected') === 'true') {
      setActiveTab('account');
      setHighlightMediaAccess(true);
      // Remove query parameter from URL
      navigate('/settings?tab=account', { replace: true });
      // Fetch fresh profile data to update instagram_access_token in Redux
      fetchFreshProfile();

      // Scroll to media access section smoothly and highlight it
      setTimeout(() => {
        mediaAccessRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);

      // Remove highlight ring after 3.5 seconds
      const timer = setTimeout(() => {
        setHighlightMediaAccess(false);
      }, 3800);
      return () => clearTimeout(timer);
    }
  }, [searchParams, navigate, fetchFreshProfile]);

  // On native: listen for in-app browser close and refresh profile data
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let listenerHandle: any;
    Browser.addListener('browserFinished', async () => {
      // A short delay lets the backend finish processing the callback redirect
      await new Promise((r) => setTimeout(r, 1500));
      await fetchFreshProfile();
    }).then((handle) => {
      listenerHandle = handle;
    });

    return () => {
      listenerHandle?.remove();
    };
  }, [fetchFreshProfile]);

  const handleConnectInstagram = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Session expired. Please log in again.');
      return;
    }
    const url = `${API_BASE_URL}/instagram/connect?token=${token}`;

    if (Capacitor.isNativePlatform()) {
      // Open as an in-app browser sheet — user stays inside the app
      await Browser.open({ url, windowName: '_self' });
    } else {
      // Web: full-page redirect as before
      window.location.href = url;
    }
  };

  const handleDisconnectInstagram = () => {
    setShowDisconnectIgModal(true);
  };

  const confirmDisconnectInstagram = async () => {
    setShowDisconnectIgModal(false);
    setLoading(true);
    try {
      await userAPI.disconnectInstagram();
      // Clear access token in Redux store
      dispatch(updateUser({ instagram_access_token: undefined, instagram_token_expires_at: undefined, instagram_media_access_enabled: false }));
      toast.success('Instagram integration disconnected');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to disconnect Instagram');
    } finally {
      setLoading(false);
    }
  };
  

  const fetchBillingHistory = async () => {
    setFetchingHistory(true);
    try {
      const response = await subscriptionAPI.getSubscriptionHistory();
      if (response.data.success) {
        setBillingOrders(response.data.data);
        setShowHistoryModal(true);
      }
    } catch (error) {
      toast.error('Failed to fetch billing history');
    } finally {
      setFetchingHistory(false);
    }
  };

  const handleBusinessLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    setLogoUploading(true);
    try {
      // Compress image to ~500KB
      const compressedFile = await compressImage(file, { maxSizeMB: 0.5 });
      const formData = new FormData();
      formData.append('image', compressedFile);

      const response = await userAPI.uploadPhoto('business_logo', formData);
      if (response.data.success && (response.data.data?.url || response.data.data?.signed_url)) {
        const newUrl = response.data.data.signed_url || response.data.data.url;
        setBusinessLogo(newUrl);
        dispatch(updateUser({ business_logo: response.data.data.url, business_logo_signed: response.data.data.signed_url }));
        toast.success(response.data.message || 'Logo uploaded successfully');
      } else {
        toast.error('Failed to get logo URL from response');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to upload logo');
    } finally {
      setLogoUploading(false);
      // Reset input so the same file can be selected again
      if (e.target) {
        e.target.value = '';
      }
    }
  };

  const tabs = [
    { id: 'account' as TabType, name: 'Account', icon: UserCircleIcon },
    // { id: 'privacy' as TabType, name: 'Privacy', icon: EyeIcon },
    { id: 'notifications' as TabType, name: 'Notifications', icon: BellIcon },
    { id: 'preferences' as TabType, name: 'Preferences', icon: AdjustmentsHorizontalIcon },
    { id: 'security' as TabType, name: 'Security', icon: ShieldCheckIcon },
    { id: 'business' as TabType, name: 'Business Details', icon: BuildingOfficeIcon },
    { id: 'contracts' as TabType, name: 'Contract Templates', icon: DocumentTextIcon },
    { id: 'data' as TabType, name: 'Data & Backups', icon: DocumentTextIcon },
    { id: 'blocked' as TabType, name: 'Blocked Users', icon: NoSymbolIcon },
    // { id: 'referrals' as TabType, name: 'Referrals', icon: UserGroupIcon },
    // [Subscription removed] Plans & Billing tab hidden — platform is fully free.
    // { id: 'billing' as TabType, name: 'Plans & Billing', icon: CreditCardIcon },
    { id: 'support' as TabType, name: 'Support & Help', icon: ExclamationTriangleIcon },
  ];

  // Sync activeTab with URL param
  useEffect(() => {
    if (urlTab) {
      setActiveTab(urlTab as TabType);
    }
  }, [urlTab]);

  // Load notification preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const response = await userAPI.getNotificationPreferences();
        if (response.data.success) {
          setEmailNotifications(response.data.data.email_notifications);
          setPushNotifications(response.data.data.push_notifications);
        }
      } catch (error) {
        console.error('Failed to load notification preferences:', error);
      } finally {
        setPreferencesLoaded(true);
      }
    };
    loadPreferences();
  }, []);

  const handleUpdateEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Email is required');
      return;
    }
    
    setLoading(true);
    try {
      // Send all user data to prevent other fields from being reset
      await userAPI.updateProfile({
        first_name: user?.first_name,
        last_name: user?.last_name,
        bio: user?.bio,
        location: user?.location,
        latitude: user?.latitude,
        longitude: user?.longitude,
        phone: user?.phone,
        website: user?.website,
        instagram_handle: user?.instagram_handle,
        specializations: user?.specializations || [],
        experience: user?.experience,
        hourly_rate: user?.hourly_rate,
        is_available: user?.is_available,
        display_phone_number: user?.display_phone_number,
        display_email: user?.display_email,
        email, // Updated field
      });
      dispatch(updateUser({ email }));
      toast.success('Email updated successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update email');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePhone = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setLoading(true);
    try {
      // Send all user data to prevent other fields from being reset
      await userAPI.updateProfile({
        first_name: user?.first_name,
        last_name: user?.last_name,
        bio: user?.bio,
        location: user?.location,
        latitude: user?.latitude,
        longitude: user?.longitude,
        email: user?.email,
        website: user?.website,
        instagram_handle: user?.instagram_handle,
        specializations: user?.specializations || [],
        experience: user?.experience,
        hourly_rate: user?.hourly_rate,
        is_available: user?.is_available,
        display_phone_number: user?.display_phone_number,
        display_email: user?.display_email,
        phone, // Updated field
      });
      dispatch(updateUser({ phone }));
      toast.success('Phone number updated successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update phone');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    try {
      await userAPI.changePassword(currentPassword, newPassword);
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePrivacy = async () => {
    setLoading(true);
    try {
      // Send all user data to prevent other fields from being reset
      await userAPI.updateProfile({
        first_name: user?.first_name,
        last_name: user?.last_name,
        bio: user?.bio,
        location: user?.location,
        latitude: user?.latitude,
        longitude: user?.longitude,
        email: user?.email,
        phone: user?.phone,
        website: user?.website,
        instagram_handle: user?.instagram_handle,
        specializations: user?.specializations || [],
        experience: user?.experience,
        hourly_rate: user?.hourly_rate,
        is_available: user?.is_available,
        display_email: displayEmail, // Updated field
        display_phone_number: displayPhone, // Updated field
      });
      dispatch(updateUser({
        display_email: displayEmail,
        display_phone_number: displayPhone,
      }));
      toast.success('Privacy settings updated');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update privacy settings');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePreferences = async () => {
    setLoading(true);
    try {
      await userAPI.updateProfile({
        // ... existing fields ...
        first_name: user?.first_name,
        last_name: user?.last_name,
        bio: user?.bio,
        location: user?.location,
        latitude: user?.latitude,
        longitude: user?.longitude,
        email: user?.email,
        phone: user?.phone,
        website: user?.website,
        instagram_handle: user?.instagram_handle,
        specializations: user?.specializations || [],
        experience: user?.experience,
        hourly_rate: user?.hourly_rate,
        is_available: user?.is_available,
        display_email: user?.display_email,
        display_phone_number: user?.display_phone_number,
        image_quality_preference: imageQuality,
        default_portfolio_view: defaultPortfolioView,
        default_portfolio_tab: defaultPortfolioTab,
      });
      dispatch(updateUser({ 
        image_quality_preference: imageQuality,
        default_portfolio_view: defaultPortfolioView,
        default_portfolio_tab: defaultPortfolioTab,
      }));
      toast.success('Preferences updated successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setLoading(true);
    try {
      await dispatch(deleteAccount()).unwrap();
      dispatch(logoutUser());
      navigate('/login');
      toast.success('Account scheduled for deletion. You can revive it by logging in within 30 days.');
    } catch (error) {
      // Error handled in slice
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
    }
  };

  const handleSubmitTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticketSubject.trim() || !ticketMessage.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmittingTicket(true);
    try {
      const { supportAPI } = await import('../services/api');
      await supportAPI.submitTicket({ subject: ticketSubject, message: ticketMessage });
      toast.success('Support ticket submitted successfully. We will get back to you soon!');
      setTicketSubject('');
      setTicketMessage('');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to submit ticket');
    } finally {
      setSubmittingTicket(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Settings</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Manage your account settings and preferences</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar Tabs */}
          <div className="lg:col-span-1">
            <nav className="flex overflow-x-auto md:flex-col space-x-2 md:space-x-0 md:space-y-1 bg-white dark:bg-gray-800 rounded-lg shadow-sm p-2 scrollbar-hide border border-gray-100 dark:border-gray-700">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-shrink-0 w-auto md:w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Icon className="h-5 w-5 mr-3" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content Area */}
          <div className="lg:col-span-3">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              {/* Account Settings */}
              {activeTab === 'account' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Account Settings</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">Update your account information</p>
                  </div>

                  {/* Share Profile Card */}
                  <div className="bg-gradient-to-r from-teal-500 to-emerald-600 rounded-xl p-6 text-white shadow-lg mb-8">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-bold mb-2 flex items-center gap-2">
                          <ShareIcon className="w-5 h-5" />
                          Share Your Profile
                        </h3>
                        <p className="text-teal-50 text-sm max-w-md">
                          Generate a professional social media card to announce your presence on ClickCollab. Perfect for Instagram and LinkedIn!
                        </p>
                      </div>
                      <Button 
                        onClick={() => setShowShareModal(true)}
                        className="bg-white !text-teal-600 hover:bg-teal-50 border-none shadow-md whitespace-nowrap"
                      >
                        Create Card
                      </Button>
                    </div>
                  </div>

                  {/* Email */}
                  <form onSubmit={handleUpdateEmail} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Email Address
                      </label>
                      <div className="flex gap-3">
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="your.email@example.com"
                        />
                        <Button
                          type="submit"
                          disabled={loading || email === user?.email}
                          className="px-6"
                        >
                          Update
                        </Button>
                      </div>
                    </div>
                  </form>

                  <div className="border-t border-gray-200 dark:border-gray-700 pt-6" />

                  {/* Phone */}
                  <form onSubmit={handleUpdatePhone} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Phone Number
                      </label>
                      <div className="flex gap-3">
                        <input
                          type="tel"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="+91 9876543210"
                        />
                        <Button
                          type="submit"
                          disabled={loading || phone === user?.phone}
                          className="px-6"
                        >
                          Update
                        </Button>
                      </div>
                    </div>
                  </form>

                  <div className="border-t border-gray-200 dark:border-gray-700 pt-6" />

                  {/* Connected Services & Integrations Section */}
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Connected Services &amp; Integrations
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Manage linked cloud storage and social media integrations. Account names are displayed below for verification.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                      {/* 1. Instagram Connection Card */}
                      <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200/60 dark:border-gray-600/60 space-y-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div className="flex items-center gap-3">
                            <div className="p-2.5 bg-gradient-to-tr from-yellow-500 via-red-500 to-purple-600 rounded-lg text-white shadow-sm">
                              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.051.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                              </svg>
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                  Instagram Integration
                                </p>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  user?.instagram_access_token
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                                    : 'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
                                }`}>
                                  {user?.instagram_access_token ? 'Connected' : 'Not Connected'}
                                </span>
                              </div>
                              <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                                {user?.instagram_access_token ? (
                                  <span className="font-medium text-indigo-600 dark:text-indigo-400">
                                    Connected Account: {user?.instagram_handle ? `@${String(user.instagram_handle).replace(/^@/, '')}` : 'Instagram Creator Account'}
                                  </span>
                                ) : (
                                  'Connect your Instagram account to sync photos & reels to your portfolio.'
                                )}
                              </p>
                            </div>
                          </div>

                          <div className="flex gap-2">
                            {user?.instagram_access_token ? (
                              <Button
                                onClick={handleDisconnectInstagram}
                                variant="outline"
                                className="border-red-500 text-red-600 dark:text-red-400 hover:bg-red-500 hover:text-white dark:hover:bg-red-600 dark:hover:text-white transition-colors whitespace-nowrap text-xs py-1.5 font-medium"
                              >
                                Disconnect
                              </Button>
                            ) : (
                              <Button
                                onClick={handleConnectInstagram}
                                className="bg-gradient-to-r from-yellow-500 via-red-500 to-purple-600 hover:opacity-90 border-none text-white whitespace-nowrap text-xs py-1.5"
                              >
                                Connect Instagram
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Instagram Media Access Toggle Switch */}
                        {IS_INSTAGRAM_SYNC_ENABLED && user?.instagram_access_token && (
                          <div
                            ref={mediaAccessRef}
                            className={`mt-3 pt-3 border-t border-gray-200 dark:border-gray-600 flex items-center justify-between gap-4 rounded-xl p-3 transition-all duration-500 ${
                              highlightMediaAccess
                                ? 'ring-2 ring-indigo-500 bg-indigo-50/80 dark:bg-indigo-950/50 shadow-md scale-[1.01]'
                                : ''
                            }`}
                          >
                            <div>
                              <p className="text-xs font-semibold text-gray-900 dark:text-white">
                                Allow ClickCollab to access &amp; import media
                              </p>
                              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                Enables fetching your Instagram feed and importing photos/reels to your portfolio.
                              </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                              <input
                                type="checkbox"
                                checked={mediaAccessEnabled}
                                onChange={(e) => handleToggleMediaAccess(e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:after:border-gray-600 peer-checked:bg-indigo-600"></div>
                            </label>
                          </div>
                        )}
                      </div>

                      {/* 2. Google Drive Connection Card */}
                      <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200/60 dark:border-gray-600/60 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-white dark:bg-gray-800 rounded-xl p-2 border border-gray-200 dark:border-gray-700 shadow-sm">
                            <img src="/google-drive-logo.png" alt="Google Drive" className="w-6 h-6 object-contain" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                Google Drive Storage
                              </p>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                driveConnected
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                                  : 'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
                              }`}>
                                {driveConnected ? 'Connected' : 'Not Connected'}
                              </span>
                            </div>
                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                              {driveConnected ? (
                                <span className="font-medium text-blue-600 dark:text-blue-400">
                                  Connected Account: {driveAccountName || 'Google Drive'}
                                </span>
                              ) : (
                                'Connect Google Drive to automatically backup gallery videos and high-res shoot assets.'
                              )}
                            </p>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          {driveConnected ? (
                            <Button
                              onClick={handleConnectDrive}
                              variant="outline"
                              className="border-gray-300 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 whitespace-nowrap text-xs py-1.5"
                            >
                              Reconnect
                            </Button>
                          ) : (
                            <Button
                              onClick={handleConnectDrive}
                              className="bg-blue-600 hover:bg-blue-700 text-white whitespace-nowrap text-xs py-1.5"
                              disabled={driveLoading}
                            >
                              {driveLoading ? 'Checking...' : 'Connect Google Drive'}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Privacy Settings */}
              {activeTab === 'privacy' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Privacy Settings</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">Control what information is visible to others</p>
                  </div>

                  <div className="space-y-4">
                    {/* Display Email Toggle */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <div>
                        <h3 className="text-sm font-medium text-gray-900 dark:text-white">Display Email</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Allow others to see your email address on your profile
                        </p>
                      </div>
                      <button
                        onClick={() => setDisplayEmail(!displayEmail)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          displayEmail ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            displayEmail ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    {/* Display Phone Toggle */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <div>
                        <h3 className="text-sm font-medium text-gray-900 dark:text-white">Display Phone Number</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Allow others to see your phone number on your profile
                        </p>
                      </div>
                      <button
                        onClick={() => setDisplayPhone(!displayPhone)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          displayPhone ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            displayPhone ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  <div className="pt-4">
                    <Button
                      onClick={handleUpdatePrivacy}
                      disabled={loading}
                      className="w-full sm:w-auto"
                    >
                      Save Privacy Settings
                    </Button>
                  </div>
                </div>
              )}

              {/* Notification Settings */}
              {activeTab === 'notifications' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Notification Preferences</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">Choose how you want to be notified</p>
                  </div>

                  <div className="space-y-4">
                    {/* Email Notifications */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <div>
                        <h3 className="text-sm font-medium text-gray-900 dark:text-white">Email Notifications</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Receive email updates about your account activity
                        </p>
                      </div>
                      <button
                        onClick={() => setEmailNotifications(!emailNotifications)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          emailNotifications ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            emailNotifications ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    {/* Push Notifications */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <div>
                        <h3 className="text-sm font-medium text-gray-900 dark:text-white">Push Notifications</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Receive push notifications in your browser
                        </p>
                      </div>
                      <button
                        onClick={() => setPushNotifications(!pushNotifications)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          pushNotifications ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            pushNotifications ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  <div className="pt-4">
                    <Button
                      onClick={async () => {
                        setLoading(true);
                        try {
                          await userAPI.updateNotificationPreferences({
                            email_notifications: emailNotifications,
                            push_notifications: pushNotifications,
                          });
                          toast.success('Notification preferences saved');
                        } catch (error: any) {
                          toast.error(error.response?.data?.message || 'Failed to save preferences');
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading || !preferencesLoaded}
                      className="w-full sm:w-auto"
                    >
                      Save Notification Settings
                    </Button>
                  </div>
                </div>
              )}

              {/* General Preferences */}
              {activeTab === 'preferences' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">General Preferences</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">Customize your experience</p>
                  </div>

                  <div className="space-y-4">
                    {/* Image Quality Toggle */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <div>
                        <h3 className="text-sm font-medium text-gray-900 dark:text-white">Image Quality</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Choose "Medium" to save data and load faster, or "Original" for highest quality.
                          (Does not affect thumbnails)
                        </p>
                      </div>
                      <div className="flex items-center bg-gray-200 dark:bg-gray-700 rounded-lg p-1">
                        <button
                          onClick={() => setImageQuality('medium')}
                          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                            imageQuality === 'medium'
                              ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                          }`}
                        >
                          Medium
                        </button>
                        <button
                          onClick={() => setImageQuality('original')}
                          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                            imageQuality === 'original'
                              ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                          }`}
                        >
                          Original
                        </button>
                      </div>
                    </div>

                    {/* Default Portfolio View Toggle */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <div>
                        <h3 className="text-sm font-medium text-gray-900 dark:text-white">Default Portfolio View</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Choose whether your profile shows "Folders" or "Grid" by default to visitors.
                        </p>
                      </div>
                      <div className="flex items-center bg-gray-200 dark:bg-gray-700 rounded-lg p-1">
                        <button
                          onClick={() => setDefaultPortfolioView('folders')}
                          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                            defaultPortfolioView === 'folders'
                              ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                          }`}
                        >
                          Folders
                        </button>
                        <button
                          onClick={() => setDefaultPortfolioView('grid')}
                          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                            defaultPortfolioView === 'grid'
                              ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                          }`}
                        >
                          Grid
                        </button>
                      </div>
                    </div>

                    {/* Default Portfolio Tab */}
                    {user?.creator_sub_type === 'both' && (
                      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <div>
                          <h3 className="text-sm font-medium text-gray-900 dark:text-white">Default Portfolio Tab</h3>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Choose whether your profile shows "Photos" or "Videos" tab by default to visitors.
                          </p>
                        </div>
                        <div className="flex items-center bg-gray-200 dark:bg-gray-700 rounded-lg p-1">
                          <button
                            onClick={() => setDefaultPortfolioTab('photos')}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                              defaultPortfolioTab === 'photos'
                                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                            }`}
                          >
                            Photos
                          </button>
                          <button
                            onClick={() => setDefaultPortfolioTab('videos')}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                              defaultPortfolioTab === 'videos'
                                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                            }`}
                          >
                            Videos
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Theme Selection */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <div>
                        <h3 className="text-sm font-medium text-gray-900 dark:text-white">Theme</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Switch between light and dark mode
                        </p>
                      </div>
                      <ThemeToggle />
                    </div>
                  </div>

                  <div className="pt-4">
                    <Button
                      onClick={handleUpdatePreferences}
                      disabled={loading || (imageQuality === user?.image_quality_preference && defaultPortfolioView === user?.default_portfolio_view && defaultPortfolioTab === user?.default_portfolio_tab)}
                      className="w-full sm:w-auto"
                    >
                      Save Preferences
                    </Button>
                  </div>
                </div>
              )}

              {/* Security Settings */}
              {activeTab === 'security' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Security Settings</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">Manage your password and account security</p>
                  </div>

                  {/* Change Password */}
                  <form onSubmit={handleChangePassword} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Current Password
                      </label>
                      <input
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Enter current password"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        New Password
                      </label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Enter new password (min 8 characters)"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Confirm new password"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={loading || !currentPassword || !newPassword || !confirmPassword}
                      className="w-full sm:w-auto"
                    >
                      Change Password
                    </Button>
                  </form>

                  <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6" />

                  {/* Danger Zone */}
                  <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/50 rounded-lg p-4">
                    <div className="flex items-start">
                      <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mt-0.5 mr-3" />
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-red-900 dark:text-red-400">Danger Zone</h3>
                        <p className="text-xs text-red-700 dark:text-red-300/80 mt-1">
                          Once you delete your account, there is no going back. Please be certain.
                        </p>
                        <button
                          onClick={() => setShowDeleteModal(true)}
                          className="mt-3 text-sm font-medium text-red-600 hover:text-red-700 underline"
                        >
                          Delete Account
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Blocked Users Settings */}
              {activeTab === 'blocked' && (
                <BlockedUsersSettings />
              )}

              {/* Referrals Settings
              {activeTab === 'referrals' && (
                <ReferralsSettings />
              )} */}

              {/* Contract Templates Settings */}
              {activeTab === 'contracts' && (
                <ContractTemplatesSettings />
              )}

              {/* Data & Backup Settings */}
              {activeTab === 'data' && (
                <DataBackupSettings />
              )}

              {/* Support & Help */}
              {activeTab === 'support' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Support & Help</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">Need help? We're here for you.</p>
                  </div>

                  {/* Privacy Policy quick link */}
                  <Link
                    to="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-4 rounded-xl bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-100 dark:border-indigo-900/40 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <DocumentTextIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                      <div>
                        <p className="text-sm font-semibold text-indigo-700 dark:text-indigo-300">Privacy Policy</p>
                        <p className="text-xs text-indigo-500 dark:text-indigo-400 mt-0.5">Learn how we handle your personal data</p>
                      </div>
                    </div>
                    <ArrowTopRightOnSquareIcon className="w-4 h-4 text-indigo-400 group-hover:text-indigo-600 dark:group-hover:text-indigo-300 transition-colors" />
                  </Link>

                  <form onSubmit={handleSubmitTicket} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Subject</label>
                      <input
                        type="text"
                        value={ticketSubject}
                        onChange={(e) => setTicketSubject(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="What can we help you with?"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Message</label>
                      <textarea
                        value={ticketMessage}
                        onChange={(e) => setTicketMessage(e.target.value)}
                        rows={5}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Describe your issue in detail..."
                      />
                    </div>
                    <Button type="submit" disabled={submittingTicket} className="w-full sm:w-auto">
                      Submit Ticket
                    </Button>
                  </form>

                  {/* <div className="mt-12 p-6 bg-primary-50 dark:bg-primary-900/10 rounded-2xl border border-primary-100 dark:border-primary-800">
                    <h3 className="text-sm font-bold text-primary-900 dark:text-primary-400 uppercase tracking-wider mb-2">Self Help</h3>
                    <p className="text-sm text-primary-700 dark:text-primary-300 mb-4">
                      Check out our frequently asked questions for quick answers to common issues.
                    </p>
                    <button 
                      onClick={() => navigate('/facts')}
                      className="text-sm font-bold text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                    >
                      View FAQ <ChevronRightIcon className="w-4 h-4" />
                    </button>
                  </div> */}
                </div>
              )}

              {/* [Subscription removed] Billing Tab hidden — platform is fully free. */}
              {/*{false && activeTab === 'billing' && (*/}
              {/*  <div className="space-y-6">*/}
              {/*    <div>*/}
              {/*      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Plans &amp; Billing</h2>*/}
              {/*      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">Manage your subscription and payments</p>*/}
              {/*    </div>*/}

              {/*    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden shadow-sm">*/}
              {/*      <div className="p-6 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-700/30">*/}
              {/*        <div className="flex items-center justify-between">*/}
              {/*          <div>*/}
              {/*            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Current Plan</p>*/}
              {/*            <h3 className="text-2xl font-black text-gray-900 dark:text-white capitalize">*/}
              {/*              {user?.subscription_tier || 'Basic'}*/}
              {/*            </h3>*/}
              {/*          </div>*/}
              {/*          <div className="flex items-center gap-2">*/}
              {/*             <span className="px-3 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 text-[10px] font-black uppercase tracking-widest rounded-full">*/}
              {/*               Active*/}
              {/*             </span>*/}
              {/*          </div>*/}
              {/*        </div>*/}
              {/*      </div>*/}
              {/*      */}
              {/*      <div className="p-6 space-y-6">*/}
              {/*        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">*/}
              {/*          <div className="space-y-1">*/}
              {/*            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Next Billing Date</p>*/}
              {/*            <p className="text-base font-bold text-gray-900 dark:text-white">*/}
              {/*              {user?.subscription_expires_at ? new Date(user?.subscription_expires_at as any).toLocaleDateString() : 'N/A'}*/}
              {/*            </p>*/}
              {/*          </div>*/}
              {/*          <div className="space-y-1">*/}
              {/*            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Billing Cycle</p>*/}
              {/*            <p className="text-base font-bold text-gray-900 dark:text-white">Monthly</p>*/}
              {/*          </div>*/}
              {/*        </div>*/}

              {/*        <div className="pt-4 flex flex-col sm:flex-row gap-4">*/}
              {/*          <Button */}
              {/*            onClick={() => navigate('/pricing')}*/}
              {/*            className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-600/20"*/}
              {/*          >*/}
              {/*            {user?.subscription_tier?.toLowerCase() === 'premium' ? 'Manage Subscription' : 'Upgrade Plan'}*/}
              {/*          </Button>*/}
              {/*          <Button */}
              {/*            variant="ghost"*/}
              {/*            onClick={fetchBillingHistory}*/}
              {/*            disabled={fetchingHistory}*/}
              {/*            className="flex-1 border border-gray-200 dark:border-gray-700 h-10"*/}
              {/*          >*/}
              {/*            {fetchingHistory ? 'Loading...' : 'View Invoices'}*/}
              {/*          </Button>*/}
              {/*        </div>*/}
              {/*      </div>*/}
              {/*    </div>*/}

              {/*    /!* Payment Methods Placeholder *!/*/}
              {/*    <div className="p-6 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-dashed border-gray-300 dark:border-gray-600 text-center">*/}
              {/*      <p className="text-sm text-gray-500 dark:text-gray-400">*/}
              {/*        Payment methods are managed securely via our payment processor. */}
              {/*        You can update your card details during the checkout process.*/}
              {/*      </p>*/}
              {/*    </div>*/}
              {/*   </div>*/}
              {/*)}*/}

              {/* Business Settings */}
              {activeTab === 'business' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Business Details</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">These details will be used for your professional invoices</p>
                  </div>

                  <form 
                    onSubmit={async (e) => {
                      e.preventDefault();
                      setLoading(true);
                      try {
                      await userAPI.updateProfile({
                          ...user,
                          company_name: companyName,
                          gst_number: gstNumber,
                          gst_percentage: Number(gstPercentage),
                          business_logo: businessLogo,
                          address: address,
                        });
                        dispatch(updateUser({ 
                          company_name: companyName, 
                          gst_number: gstNumber, 
                          gst_percentage: Number(gstPercentage),
                          business_logo: businessLogo,
                          address: address,
                        }));
                        toast.success('Business details updated successfully');
                      } catch (error: any) {
                        toast.error(error.response?.data?.message || 'Failed to update business details');
                      } finally {
                        setLoading(false);
                      }
                    }} 
                    className="space-y-6"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                      {/* Business Logo Upload */}
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                          {isModel ? 'Branding / Model Headshot' : 'Business Logo'}
                        </label>
                        <div className="flex items-center gap-5">
                          <div className="w-20 h-20 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-600 overflow-hidden flex items-center justify-center bg-gray-50 dark:bg-gray-800 flex-shrink-0">
                            {businessLogo ? (
                              <img src={resolveImageUrl(businessLogo)} alt="Business Logo" className="w-full h-full object-contain" />
                            ) : (
                              <BuildingOfficeIcon className="w-8 h-8 text-gray-300" />
                            )}
                          </div>
                          <div>
                            <label
                              htmlFor="business-logo-upload"
                              className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                            >
                              {logoUploading ? 'Uploading...' : businessLogo ? 'Change Logo' : 'Upload Logo'}
                            </label>
                            <input id="business-logo-upload" type="file" accept="image/*" className="hidden" onChange={handleBusinessLogoChange} />
                            {businessLogo && (
                              <button
                                type="button"
                                onClick={() => setBusinessLogo('')}
                                className="ml-3 text-sm text-red-500 hover:text-red-700"
                              >Remove</button>
                            )}
                            <p className="mt-1.5 text-xs text-gray-400">PNG, JPG or SVG, max 2MB. Appears on invoices.</p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {isModel ? 'Stage Name / Agency' : 'Company / Studio Name'}
                        </label>
                        <input
                          type="text"
                          value={companyName}
                          onChange={(e) => setCompanyName(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder={isModel ? "e.g. Agency Name or Your Brand" : "e.g. Creative Studios"}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          GST Number (Optional)
                        </label>
                        <input
                          type="text"
                          value={gstNumber}
                          onChange={(e) => setGstNumber(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="22AAAAA0000A1Z5"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Default GST Percentage (%)
                        </label>
                        <input
                          type="number"
                          value={gstPercentage}
                          onChange={(e) => setGstPercentage(Number(e.target.value))}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Business Address
                        </label>
                        <textarea
                          value={address}
                          onChange={(e) => setAddress(e.target.value)}
                          rows={3}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="Enter your full business address for invoices"
                        />
                      </div>
                    </div>

                    <div className="pt-4">
                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full sm:w-auto"
                      >
                        {loading ? 'Saving...' : 'Save Business Details'}
                      </Button>
                    </div>

                    <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800/50 rounded-lg p-4 mt-8">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <AdjustmentsHorizontalIcon className="h-5 w-5 text-blue-400" />
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300">Invoice Configuration</h3>
                          <p className="mt-2 text-sm text-blue-700 dark:text-blue-400/80">
                            These details will automatically appear on all future invoices. You can toggle GST inclusion per invoice during creation.
                          </p>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal for Disconnecting Instagram */}
      <ConfirmationModal
        isOpen={showDisconnectIgModal}
        onClose={() => setShowDisconnectIgModal(false)}
        onConfirm={confirmDisconnectInstagram}
        title="Disconnect Instagram?"
        message={
          <div className="space-y-1">
            <p>Disconnecting will disable importing Instagram photos & reels.</p>
            <p>Your media access settings will be reset.</p>
          </div>
        }
        confirmText="Disconnect"
        cancelText="Keep Connected"
        type="danger"
      />

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500/75 dark:bg-gray-900/80 backdrop-blur-sm"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full border border-gray-100 dark:border-gray-700">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-500" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="modal-title">
                      Delete Account
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to delete your account? This action will schedule your account for deletion.
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                        <strong>Grace Period:</strong> Your account will be permanently deleted in <strong>30 days</strong>. If you change your mind, simply log in within this period to revive your account.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-100 dark:border-gray-700">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleDeleteAccount}
                  disabled={loading}
                >
                  {loading ? 'Deleting...' : 'Delete Account'}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Social Share Modal */}
      <SocialShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
      />

      {/* Billing History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={() => setShowHistoryModal(false)}>
              <div className="absolute inset-0 bg-gray-500/75 dark:bg-gray-900/80 backdrop-blur-sm"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-3xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full border border-gray-100 dark:border-gray-700">
              <div className="bg-white dark:bg-gray-800 px-6 pt-6 pb-6 shadow-sm border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
                <div>
                   <h3 className="text-xl font-black text-gray-900 dark:text-white leading-tight">Billing History</h3>
                   <p className="text-sm font-medium text-gray-500">Your recent subscription invoices</p>
                </div>
                <button 
                  onClick={() => setShowHistoryModal(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
                >
                  <XMarkIcon className="w-6 h-6 text-gray-400" />
                </button>
              </div>

              <div className="max-h-[60vh] overflow-y-auto p-6">
                {billingOrders.length > 0 ? (
                  <div className="space-y-4">
                    {billingOrders.map((order) => (
                      <div key={order.id} className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-2xl border border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                         <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-white dark:bg-gray-800 shadow-sm flex items-center justify-center border border-gray-100 dark:border-gray-700">
                               <CreditCardIcon className="w-6 h-6 text-indigo-500" />
                            </div>
                            <div>
                               <p className="font-black text-gray-900 dark:text-white capitalize">
                                 {order.plan_tier === 'pro' ? 'Pro Plan' : order.plan_tier === 'premium' ? 'Premium Plan' : order.plan_tier}
                               </p>
                               <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                                 {new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                               </p>
                            </div>
                         </div>
                         
                         <div className="flex items-center justify-between sm:justify-end gap-6">
                            <div className="text-right">
                               <p className="font-black text-gray-900 dark:text-white text-lg">₹{order.amount}</p>
                               <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight capitalize">{order.billing_cycle}</p>
                            </div>
                             <span className="px-3 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 text-[10px] font-black uppercase tracking-widest rounded-full">
                                Paid
                             </span>
                             <Link 
                                to={`/settings/subscription-invoice/${order.id}`}
                                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors group/invoice"
                             >
                                <ArrowTopRightOnSquareIcon className="w-5 h-5 text-gray-400 group-hover/invoice:text-primary-500" />
                             </Link>
                          </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                     <div className="w-16 h-16 bg-gray-50 dark:bg-gray-700/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <CreditCardIcon className="w-8 h-8 text-gray-300 dark:text-gray-600" />
                     </div>
                     <h4 className="font-bold text-gray-900 dark:text-white">No invoices yet</h4>
                     <p className="text-sm text-gray-500 mt-1">When you subscribe to a paid plan, your invoices will appear here.</p>
                  </div>
                )}
              </div>
              
              <div className="p-6 bg-gray-50 dark:bg-gray-900/30 border-t border-gray-100 dark:border-gray-700 text-center">
                 <p className="text-xs font-medium text-gray-500">
                    Need help with a payment? <button onClick={() => { setShowHistoryModal(false); setActiveTab('support'); }} className="text-indigo-600 hover:underline font-bold">Contact Support</button>
                 </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function BlockedUsersSettings() {
  const [blockedUsers, setBlockedUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlockedUsers();
  }, []);

  const fetchBlockedUsers = async () => {
    try {
      const users = await userAPI.getBlockedUsers();
      setBlockedUsers(users);
    } catch (error) {
      console.error('Failed to fetch blocked users:', error);
      toast.error('Failed to load blocked users');
    } finally {
      setLoading(false);
    }
  };

  const handleUnblock = async (userId: string) => {
    if (!window.confirm('Are you sure you want to unblock this user?')) return;

    try {
      await userAPI.unblockUser(userId);
      toast.success('User unblocked successfully');
      setBlockedUsers(prev => prev.filter(u => u.id !== userId));
    } catch (error) {
      toast.error('Failed to unblock user');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-black"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Blocked Users</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Manage users you have blocked. They cannot message you or see your profile.
        </p>
      </div>

      {blockedUsers && blockedUsers.length > 0 ? (
        <div className="space-y-4">
          {blockedUsers.map((user) => (
            <div key={user.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-100 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <Avatar 
                  src={user.profile_pic_thumb_signed || user.profile_pic} 
                  alt={`${user.first_name} ${user.last_name}`}
                  size="md"
                />
                <div>
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                    {user.first_name} {user.last_name}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{user.user_type}</p>
                </div>
              </div>
              <button
                onClick={() => handleUnblock(user.id)}
                className="px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-md transition-colors"
              >
                Unblock
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-dashed border-gray-200 dark:border-gray-700">
          <NoSymbolIcon className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No blocked users</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">You haven't blocked anyone yet.</p>
        </div>
      )}
    </div>
  );
};

// function ReferralsSettings() {
//   const { user } = useAppSelector((state) => state.auth);
//   const [referralData, setReferralData] = useState<any>(null);
//   const [loading, setLoading] = useState(true);
//   const [qualifiedCount, setQualifiedCount] = useState(0);
//   const [nextTierGoal, setNextTierGoal] = useState(5);
//   const [currentTierReward, setCurrentTierReward] = useState("1 Month Free Premium");
//   const [progress, setProgress] = useState(0);

//   // Claim State
//   const [referralCodeToClaim, setReferralCodeToClaim] = useState('');
//   const [claimLoading, setClaimLoading] = useState(false);

//   const isHirer = user?.user_type === 'hirer';

//   // useEffect(() => {
//   //   fetchReferrals();
//   // }, [user?.user_type]); // Re-fetch if user type changes

//   // const fetchReferrals = async () => {
//   //   try {
//   //     const response = await userAPI.getReferrals();
//   //     if (response.data.success) {
//   //       const data = response.data.data;
//   //       setReferralData(data);
//   //       const count = data.qualified_referrals_count || 0;
//   //       setQualifiedCount(count);

//   //       // Calculate progress to next tier
//   //       let calculatedNextTierGoal = 5;
//   //       let calculatedCurrentTierReward = "1 Month Free Premium";
//   //       let calculatedProgress = 0;

//   //       if (isHirer) {
//   //         // Hirer Logic (Coins)
//   //         // ... (existing hirer logic)
//   //       } else {
//   //         // Creator/Model Logic (Pro Bonus)
//   //         if (count >= 10) {
//   //           calculatedNextTierGoal = 10;
//   //           calculatedCurrentTierReward = "3 Months FREE Pro Membership";
//   //           calculatedProgress = ((count - 5) / 5) * 100;
//   //         } else if (count >= 5) {
//   //           calculatedNextTierGoal = 10;
//   //           calculatedCurrentTierReward = "2 Months FREE Pro Membership";
//   //           calculatedProgress = ((count - 5) / 5) * 100;
//   //         } else if (count >= 2) {
//   //           calculatedNextTierGoal = 5;
//   //           calculatedCurrentTierReward = "1 Month FREE Pro Membership";
//   //           calculatedProgress = ((count - 2) / 3) * 100;
//   //         } else {
//   //           calculatedNextTierGoal = 2;
//   //           calculatedCurrentTierReward = "1 Month FREE Pro Membership (at 2 refs)";
//   //           calculatedProgress = (count / 2) * 100;
//   //         }
//   //       }
//   //       setNextTierGoal(calculatedNextTierGoal);
//   //       setCurrentTierReward(calculatedCurrentTierReward);
//   //       setProgress(calculatedProgress);
//   //     }
//   //   } catch (error) {
//   //     console.error('Failed to fetch referrals:', error);
//   //     toast.error('Failed to load referrals');
//   //   } finally {
//   //     setLoading(false);
//   //   }
//   // };

//   const copyToClipboard = () => {
//     if (referralData?.referral_code) {
//       navigator.clipboard.writeText(referralData.referral_code);
//       toast.success('Referral code copied to clipboard!');
//     }
//   };

//   const handleClaimReferral = async (e: React.FormEvent) => {
//     e.preventDefault();
//     if (!referralCodeToClaim.trim()) return;

//     setClaimLoading(true);
//     try {
//       await userAPI.claimReferral(referralCodeToClaim.trim());
//       toast.success('Referral code claimed successfully!');
//       setReferralCodeToClaim('');
//       // fetchReferrals();
//     } catch (error: any) {
//       toast.error(error.response?.data?.error || 'Failed to claim referral code');
//     } finally {
//       setClaimLoading(false);
//     }
//   };

//   if (loading) {
//     return (
//       <div className="flex justify-center py-8">
//         <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-black"></div>
//       </div>
//     );
//   }

//   return (
//     <div className="space-y-6">
//       <div>
//         <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Referral Program</h2>
//         <p className="text-sm text-gray-600 dark:text-gray-400">
//           {isHirer 
//             ? "Invite fellow Creators and Models to join ClickCollab. Earn ClickCoins when they qualify!"
//             : "Invite creators to join ClickCollab. Get 1 Month of Pro for FREE when your friend upgrades to Pro or Premium!"
//           }
//         </p>
//         <div className="mt-4 p-4 bg-teal-50 dark:bg-teal-900/20 rounded-lg text-sm text-teal-800 dark:text-teal-300 border border-teal-100 dark:border-teal-800/30">
//           <p className="font-semibold mb-1">How it works:</p>
//           <ul className="list-disc list-inside space-y-1 opacity-90 text-xs">
//              <li>Share your unique referral code.</li>
//              <li>Your friend joins and completes their profile.</li>
//              <li>Post first <strong>Live</strong> Project (Creators/Models)</li>
//              <li><strong>Reward:</strong> Earn Pro membership rewards based on how many friends you refer!</li>
//           </ul>
//         </div>
//       </div>

//       {/* Claim Referral Code Section - Only show if not already referred */}
//       {!user?.referred_by && (
//         <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
//           <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Have a Referral Code?</h3>
//           <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
//             Enter a referral code from a friend to help them unlock rewards.
//           </p>
//           <form onSubmit={handleClaimReferral} className="flex gap-3">
//             <input
//               type="text"
//               value={referralCodeToClaim}
//               onChange={(e) => setReferralCodeToClaim(e.target.value)}
//               placeholder="Enter referral code"
//               className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
//             />
//             <Button
//               type="submit"
//               disabled={claimLoading || !referralCodeToClaim.trim()}
//               className="px-6"
//             >
//               {claimLoading ? 'Claiming...' : 'Claim'}
//             </Button>
//           </form>
//         </div>
//       )}

//       {/* {!isHirer && (
//         <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
//           <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Referral Rewards</h3>
//           <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//             <div className={`p-4 rounded-xl border ${qualifiedCount >= 2 ? 'bg-teal-50 dark:bg-teal-900/20 border-teal-100 dark:border-teal-800' : 'bg-gray-50 dark:bg-gray-700/50 border-gray-100 dark:border-gray-700'}`}>
//               <div className="flex items-center space-x-3 mb-2">
//                 <StarIcon className={`w-5 h-5 ${qualifiedCount >= 2 ? 'text-teal-600' : 'text-gray-400'}`} />
//                 <span className={`font-bold ${qualifiedCount >= 2 ? 'text-teal-900 dark:text-teal-100' : 'text-gray-500'}`}>2 Referrals</span>
//               </div>
//               <p className={`text-xs ${qualifiedCount >= 2 ? 'text-teal-700 dark:text-teal-300' : 'text-gray-400'}`}>1 Month Pro FREE</p>
//             </div>
//             <div className={`p-4 rounded-xl border ${qualifiedCount >= 5 ? 'bg-teal-50 dark:bg-teal-900/20 border-teal-100 dark:border-teal-800' : 'bg-gray-50 dark:bg-gray-700/50 border-gray-100 dark:border-gray-700'}`}>
//               <div className="flex items-center space-x-3 mb-2">
//                 <StarIcon className={`w-5 h-5 ${qualifiedCount >= 5 ? 'text-teal-600' : 'text-gray-400'}`} />
//                 <span className={`font-bold ${qualifiedCount >= 5 ? 'text-teal-900 dark:text-teal-100' : 'text-gray-500'}`}>5 Referrals</span>
//               </div>
//               <p className={`text-xs ${qualifiedCount >= 5 ? 'text-teal-700 dark:text-teal-300' : 'text-gray-400'}`}>2 Months Pro FREE</p>
//             </div>
//             <div className={`p-4 rounded-xl border ${qualifiedCount >= 10 ? 'bg-teal-50 dark:bg-teal-900/20 border-teal-100 dark:border-teal-800' : 'bg-gray-50 dark:bg-gray-700/50 border-gray-100 dark:border-gray-700'}`}>
//               <div className="flex items-center space-x-3 mb-2">
//                 <StarIcon className={`w-5 h-5 ${qualifiedCount >= 10 ? 'text-teal-600' : 'text-gray-400'}`} />
//                 <span className={`font-bold ${qualifiedCount >= 10 ? 'text-teal-900 dark:text-teal-100' : 'text-gray-500'}`}>10+ Referrals</span>
//               </div>
//               <p className={`text-xs ${qualifiedCount >= 10 ? 'text-teal-700 dark:text-teal-300' : 'text-gray-400'}`}>3 Months Pro FREE</p>
//             </div>
//           </div>
//           <p className="mt-4 text-xs text-gray-500 dark:text-gray-400 italic">
//             * Awarded when referred friends complete their profile and post their first live project.
//           </p>
//         </div>
//       )} */}

//       {/* Referral Tiers (Only for Hirers) */}
//       {/* {isHirer && (
//         <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
//           <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Referral Tiers & Rewards</h3>
//           <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
//             You have <span className="font-semibold text-primary-600">{qualifiedCount}</span> qualified referrals.
//             {qualifiedCount < nextTierGoal && (
//               <span> Refer <span className="font-semibold text-primary-600">{nextTierGoal - qualifiedCount}</span> more to unlock <span className="font-semibold text-primary-600">{currentTierReward}</span>!</span>
//             )}
//             {qualifiedCount >= nextTierGoal && (
//               <span> You've reached the current tier! Next reward: <span className="font-semibold text-primary-600">{currentTierReward}</span></span>
//             )}
//           </p>

//           <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mb-4">
//             <div 
//               className="bg-primary-600 h-2.5 rounded-full transition-all duration-500 ease-out" 
//               style={{ width: `${progress}%` }}
//             ></div>
//           </div>

//           <div className="grid grid-cols-3 gap-4 mt-6 text-center text-xs text-gray-500 dark:text-gray-400">
//             <div className={`p-2 rounded-lg ${qualifiedCount >= 5 ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' : ''}`}>
//               <p className="font-bold text-sm mb-1">5 Referrals</p>
//               <p>50 Coins</p>
//             </div>
//             <div className={`p-2 rounded-lg ${qualifiedCount >= 10 ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' : ''}`}>
//               <p className="font-bold text-sm mb-1">10 Referrals</p>
//               <p>150 Coins</p>
//             </div>
//             <div className={`p-2 rounded-lg ${qualifiedCount >= 15 ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' : ''}`}>
//               <p className="font-bold text-sm mb-1">15+ Referrals</p>
//               <p>+10 Coins/Ref</p>
//             </div>
//           </div>
//         </div>
//       )} */}

//       {/* Referral Stats Cards */}
//       <div className={`grid grid-cols-1 ${isHirer ? 'md:grid-cols-2' : ''} gap-6`}>
//         {/* Referral Code Card */}
//         <div className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-2xl p-6 text-white shadow-lg">
//           <div className="flex flex-col justify-between h-full">
//             <div>
//               <h3 className="text-lg font-bold mb-2">Your Referral Code</h3>
//               <p className="text-purple-100 text-sm mb-4">Share this code with your friends.</p>
//               <div className="flex items-center bg-white/20 backdrop-blur-sm rounded-lg p-1 pr-2 w-fit">
//                 <code className="px-3 py-1.5 font-mono text-lg font-bold tracking-wider">{referralData?.referral_code || 'Generating...'}</code>
//                 <button 
//                   onClick={copyToClipboard}
//                   className="ml-2 p-1.5 hover:bg-white/20 rounded-md transition-colors"
//                   title="Copy to clipboard"
//                 >
//                   <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
//                     <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184" />
//                   </svg>
//                 </button>
//               </div>
//             </div>
//             <div className="mt-6">
//               <div className="text-3xl font-bold">{referralData?.referral_count || 0}</div>
//               <div className="text-purple-100 text-sm font-medium">Friends Invited</div>
//             </div>
//           </div>
//         </div>

//         {/* ClickCoins Card - Only for Hirers */}
//         {isHirer && (
//           <div className="bg-gradient-to-r from-amber-400 to-orange-500 rounded-2xl p-6 text-white shadow-lg">
//             <div className="flex flex-col justify-between h-full">
//               <div>
//                 <h3 className="text-lg font-bold mb-2">ClickCoins Earned</h3>
//                 <p className="text-amber-100 text-sm mb-4">Earn 10 coins for every friend you invite!</p>
//               </div>
//               <div className="flex items-end justify-between mt-4">
//                 <div>
//                   <div className="text-4xl font-bold mb-1">{user?.click_coins || 0}</div>
//                   <div className="text-amber-100 text-sm font-medium">Total Coins</div>
//                 </div>
//                 <div className="bg-white/20 p-3 rounded-full">
//                   <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-yellow-100">
//                     <path fillRule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z" clipRule="evenodd" />
//                   </svg>
//                 </div>
//               </div>
//             </div>
//           </div>
//         )}
//       </div>

//       {/* Referred Users List */}
//       <div>
//         <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Referred Users</h3>
//         {referralData?.referred_users && referralData.referred_users.length > 0 ? (
//           <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
//             <ul className="divide-y divide-gray-100 dark:divide-gray-700">
//               {referralData.referred_users.map((user: any) => (
//                 <li key={user.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
//                   <div className="flex items-center space-x-4">
//                     <Avatar src={user.profile_pic_thumb_signed || user.profile_pic} alt={user.first_name} size="md" />
//                     <div className="flex-1 min-w-0">
//                       <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
//                         {user.first_name} {user.last_name}
//                       </p>
//                       <p className="text-xs text-gray-500 dark:text-gray-400">
//                         Joined {new Date(user.created_at).toLocaleDateString()}
//                       </p>
//                     </div>
//                   </div>
//                 </li>
//               ))}
//             </ul>
//           </div>
//         ) : (
//           <div className="text-center py-12 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-700">
//             <UserGroupIcon className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" />
//             <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No referrals yet</h3>
//             <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Share your code to start inviting friends!</p>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

function ContractTemplatesSettings() {
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState({ id: '', title: '', body: '' });
  const [showPlatformDefaults, setShowPlatformDefaults] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const { data } = await crmAPI.getContractTemplates();
      setTemplates(data.data.templates || []);
    } catch (error) {
      toast.error('Failed to load contract templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentTemplate.title.trim() || !currentTemplate.body.trim()) {
      toast.error('Title and body are required');
      return;
    }

    try {
      const sanitizedBody = currentTemplate.body.replace(/href="(?!(http|https|mailto:|tel:|\/|#))(.*?)"/g, 'href="https://$2"');

      if (currentTemplate.id) {
        await crmAPI.updateContractTemplate(currentTemplate.id, {
          title: currentTemplate.title,
          body: sanitizedBody
        });
        toast.success('Template updated');
      } else {
        await crmAPI.createContractTemplate({
          title: currentTemplate.title,
          body: sanitizedBody
        });
        toast.success('Template created');
      }
      setIsEditing(false);
      setCurrentTemplate({ id: '', title: '', body: '' });
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await crmAPI.deleteContractTemplate(id);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600"></div></div>;
  }

  if (isEditing) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {currentTemplate.id ? 'Edit Template' : 'New Template'}
          </h2>
          <button onClick={() => setIsEditing(false)} className="text-sm text-gray-500 hover:text-gray-700">Cancel</button>
        </div>
        
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Template Name</label>
            <input
              type="text"
              required
              value={currentTemplate.title}
              onChange={e => setCurrentTemplate({...currentTemplate, title: e.target.value})}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              placeholder="e.g. Standard Wedding Contract"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contract Body</label>
            <p className="text-xs text-gray-500 mb-2">
              Available variables (will auto-fill): {'{{client_name}}'}, {'{{project_date}}'}, {'{{project_total}}'}, {'{{advance_amount}}'}
            </p>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-300 dark:border-gray-600 quill-modern min-h-[300px] flex flex-col">
              <ReactQuill
                theme="snow"
                value={currentTemplate.body}
                onChange={val => setCurrentTemplate({...currentTemplate, body: val})}
                placeholder="This Agreement is made between {{client_name}} and the Photographer..."
                className="h-[250px]"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
            <Button type="button" variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
            <Button type="submit">Save Template</Button>
          </div>
        </form>
      </div>
    );
  }

  const NIL_UUID = '00000000-0000-0000-0000-000000000000';
  const platformTemplates = templates.filter(t => !t.owner_id || t.owner_id === NIL_UUID);
  const userTemplates = templates.filter(t => t.owner_id && t.owner_id !== NIL_UUID);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Contract Templates</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Create reusable boilerplate text for your client agreements.
          </p>
        </div>
        <Button onClick={() => {
          setCurrentTemplate({ id: '', title: '', body: '' });
          setIsEditing(true);
        }} className="flex items-center gap-2">
          <PlusIcon className="w-4 h-4" />
          New Template
        </Button>
      </div>

      <div className="space-y-8">
        {/* Platform Defaults Section */}
        {platformTemplates.length > 0 && (
          <div className="bg-gray-50/50 dark:bg-gray-800/20 rounded-[2.5rem] border border-gray-100 dark:border-gray-800 p-8 transition-all">
            <button 
                onClick={() => setShowPlatformDefaults(!showPlatformDefaults)}
                className="w-full flex justify-between items-center group"
            >
                <div className="flex flex-col items-start">
                    <h3 className="text-xs font-black uppercase tracking-widest text-teal-600 mb-1">ClickCollab Defaults</h3>
                    <p className="text-xs text-gray-500 font-medium">Standard boilerplate contracts you can use or duplicate.</p>
                </div>
                <div className={`p-2 rounded-full bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-sm transition-all group-hover:scale-110 ${showPlatformDefaults ? 'rotate-180' : ''}`}>
                    <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                </div>
            </button>
            
            {showPlatformDefaults && (
                <div className="mt-8 space-y-4 animate-in fade-in slide-in-from-top-4 duration-300">
                {platformTemplates.map(t => (
                    <div key={t.id} className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 flex justify-between items-center group shadow-sm">
                    <div>
                        <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-gray-900 dark:text-white">{t.title}</h4>
                        <span className="px-2 py-0.5 rounded-full bg-teal-50 text-teal-600 text-[10px] font-bold">Standard</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1 truncate max-w-xl">{t.body.substring(0, 100).replace(/<[^>]+>/g, '')}...</p>
                    </div>
                    <div className="flex gap-2">
                        <button
                        onClick={() => {
                            setCurrentTemplate({ id: '', title: `Copy of ${t.title}`, body: t.body });
                            setIsEditing(true);
                        }}
                        className="px-4 py-2 bg-gray-900 text-white text-xs font-black uppercase tracking-widest rounded-xl hover:bg-black shadow-lg shadow-black/10 transition-all active:scale-95"
                        >
                        Duplicate
                        </button>
                    </div>
                    </div>
                ))}
                </div>
            )}
          </div>
        )}

        {/* User Templates Section */}
        <div>
          <h3 className="text-xs font-black uppercase tracking-widest text-gray-500 mb-4 ml-1">Your Templates</h3>
          <div className="space-y-4">
            {userTemplates.map(t => (
              <div key={t.id} className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 flex justify-between items-center group shadow-sm hover:shadow-md transition-shadow">
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">{t.title}</h4>
                  <p className="text-xs text-gray-500 mt-1 truncate max-w-xl">{t.body.substring(0, 100).replace(/<[^>]+>/g, '')}...</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setCurrentTemplate({ id: t.id, title: t.title, body: t.body });
                      setIsEditing(true);
                    }}
                    className="px-4 py-2 text-xs font-black uppercase tracking-widest text-gray-500 hover:text-teal-600 transition-colors"
                  >
                    Edit
                  </button>
                  <div className="w-px h-4 bg-gray-200 dark:bg-gray-700"></div>
                  <button
                    onClick={() => handleDelete(t.id)}
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                    title="Delete"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
            {userTemplates.length === 0 && (
              <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-dashed border-gray-300 dark:border-gray-700">
                <DocumentTextIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <p className="text-sm font-bold text-gray-900 dark:text-white">No custom templates yet</p>
                <p className="text-xs text-gray-500 mt-1">Create your own or duplicate a ClickCollab default.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}



function DataBackupSettings() {
  const [downloading, setDownloading] = useState(false);

  const handleExport = async () => {
    setDownloading(true);
    try {
      const response = await crmAPI.exportCRMData();
      // Browser will handle the blob download response natively or we can trigger it:
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'crm_export.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Backup downloaded successfully');
    } catch (error) {
      toast.error('Failed to download backup');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Data & Backups</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Export your CRM data as a CSV file for local backup.
          </p>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center space-x-4 mb-4">
          <div className="p-3 bg-teal-50 dark:bg-teal-900 rounded-full">
            <DocumentTextIcon className="w-6 h-6 text-teal-600 dark:text-teal-400" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 dark:text-white">CRM Data Export</h3>
            <p className="text-xs text-gray-500">Includes all Leads, Clients, Projects, and Invoices.</p>
          </div>
        </div>
        <Button onClick={handleExport} disabled={downloading} className="w-full sm:w-auto">
          {downloading ? 'Generating...' : 'Download CSV Backup'}
        </Button>
      </div>
    </div>
  );
}

export default SettingsPage;
